import { GoalsContext } from "@/services/GoalsProvider";
import { Card, Skeleton } from "antd";
import { useContext, useMemo } from "react";
import Radial from "../../Radial";
import Image from "next/image";
import { formatNumber } from "@/utils/helper";
import useOrgSettingStore from "@/store/useOrgSettingsStore";

import diamond from "@/assets/diamond-gold.png";
import platinum from "@/assets/diamond-silver.png";
import bronze from "@/assets/diamond-bronze.png";
import silver from "@/assets/diamond-orginal.png";
import Link from "next/link";
import { PAGE_URL } from "@/utils/constants";
import { useGetMySkillOverview, useGetPointProgress } from "@/hooks/useGoals";
import useAuthStore from "@/store/useAuthStore";

export default function CurrentProgress() {
  const authUser = useAuthStore.useUser();
  const userId = authUser?.profile?.id;
  const { data: dataSkillOverview, refetch: refetchDataSkillOverview } =
    useGetMySkillOverview(userId);
  const { data: dataPointProgress, refetch: refetchPointProgress } =
    useGetPointProgress(userId);

  const isNeverSetGoal =
    dataPointProgress?.numberOfTargetCircle === 0 &&
    dataPointProgress?.numberOfTargetEvent === 0 &&
    dataPointProgress?.numberOfTargetConnection === 0;

  const { pointTiers } = useOrgSettingStore();

  const colorTiers = () => {
    const toNextTier = dataPointProgress?.rewardsProgress?.pointsBalance;

    if (pointTiers?.platinum && toNextTier >= pointTiers?.platinum)
      return { color: "#1890FF", icon: platinum };
    if (pointTiers?.gold && toNextTier >= pointTiers?.gold)
      return { color: "#FADB14", icon: diamond };
    if (pointTiers?.silver && toNextTier >= pointTiers?.silver)
      return { color: "#8C8C8C", icon: silver };
    if (pointTiers?.bronze && toNextTier >= pointTiers?.bronze)
      return { color: "#FF7875", icon: bronze };
    return { color: "#FF7875", icon: bronze };
  };

  const goalsCompletedRadialValue = Math.min(
    ((dataSkillOverview?.allGoalsCompleted || 0) /
      (dataSkillOverview?.allGoalsTarget || 1)) *
      100,
    100,
  );

  return (
    <CurrentProgressDumb
      dataPointProgress={{
        percentToNextTier:
          dataPointProgress?.rewardsProgress?.percentToNextTier,
        pointsBalance: dataPointProgress?.rewardsProgress?.pointsBalance,
        sinceLastWeek: dataPointProgress?.rewardsProgress?.sinceLastWeek,
        numberOfCircleJoined: dataPointProgress?.numberOfCircleJoined,
        numberOfConnection: dataPointProgress?.numberOfConnection,
        numberOfEventJoined: dataPointProgress?.numberOfEventJoined,
        numberOfTargetCircle: dataPointProgress?.numberOfTargetCircle,
        numberOfTargetConnection: dataPointProgress?.numberOfTargetConnection,
        numberOfTargetEvent: dataPointProgress?.numberOfTargetEvent,
      }}
      tiers={{
        color: colorTiers()?.color,
        icon: colorTiers()?.icon,
      }}
      dataSkillOverview={{
        allGoalsCompleted: isNeverSetGoal
          ? dataSkillOverview?.completedSkillCount
          : dataSkillOverview?.allGoalsCompleted,
        allGoalsTarget: isNeverSetGoal
          ? dataSkillOverview?.allSkillCount
          : dataSkillOverview?.allGoalsTarget,
        allGoalsIncrementPercentage: isNeverSetGoal
          ? dataSkillOverview?.completedSkillIncrementPercentage
          : dataSkillOverview?.allGoalsIncrementPercentage,
      }}
      goalsCompletedRadialValue={goalsCompletedRadialValue}
    />
  );
}

function CurrentProgressDumb({
  dataPointProgress,
  tiers,
  goalsCompletedRadialValue,
  dataSkillOverview,
}: {
  dataPointProgress: any;
  tiers: any;
  goalsCompletedRadialValue: any;
  dataSkillOverview: any;
}) {
  const percentNextTier = useMemo(() => {
    if (
      dataPointProgress?.percentToNextTier ||
      dataPointProgress?.percentToNextTier === 0
    )
      return 100 - dataPointProgress.percentToNextTier;
    return 0;
  }, [dataPointProgress?.percentToNextTier]);
  return (
    <Card
      title={
        <div className="flex justify-between items-center">
          <p className="font-bold">My Current Progress</p>
          <Link href={PAGE_URL.goals}>
            <span className="text-yellow text-sm">View More</span>
          </Link>
        </div>
      }
      className="w-full"
      
    >
      <div className="flex flex-row gap-11 items-center justify-center lg:justify-start h-full">
        <div className="flex flex-col justify-center items-center">
          <Radial
            loading={dataPointProgress ? false : true}
            value={100}
            size={127}
            strokeColor={tiers?.color}
            strokeWidth={6}
          >
            <Radial
              value={goalsCompletedRadialValue}
              size={88}
              strokeColor="#158284"
              strokeWidth={6}
            />
          </Radial>
          <div className="mt-4 flex flex-col items-start">
            <div className="flex gap-2 items-center justify-center">
              <div className="w-2 h-2 rounded-sm bg-[#158284]" />
              <p>Goals Completed</p>
            </div>
            {/* <div className="flex gap-2 items-center justify-center">
              <div
                className="w-2 h-2 rounded-sm"
                style={{ backgroundColor: tiers?.color }}
              />
              <p>Points Balance</p>
            </div> */}
          </div>
        </div>
        {dataPointProgress?.pointsBalance === undefined ? (
          <div className="flex flex-col gap-2">
            {new Array(4).fill(undefined).map((_, i) => (
              <Skeleton.Input active key={i} size="small" />
            ))}
          </div>
        ) : (
          <div className="flex flex-col gap-6">
            <div>
              <p className="text-sm">Goals Completed</p>
              <p className="text-2xl">
                {dataSkillOverview?.allGoalsCompleted} of{" "}
                {dataSkillOverview?.allGoalsTarget}
              </p>
              <p className="text-xs">
                Past 30 days{" "}
                {Math.ceil(dataSkillOverview?.allGoalsIncrementPercentage)}%
              </p>
            </div>
            <div>
              <p className="text-sm">Points Balance</p>
              <div className="flex items-center gap-2">
                {dataPointProgress && (
                  <Image
                    src={
                      dataPointProgress?.pointsBalance ? tiers?.icon : bronze
                    }
                    alt="diamond"
                    width={20}
                    height={20}
                  />
                )}
                <p className="text-2xl">
                  {formatNumber(dataPointProgress?.pointsBalance)}
                </p>
              </div>
              <p
                className={`text-xs ${
                  Math.ceil(dataPointProgress?.sinceLastWeek) > 0
                    ? "text-primary"
                    : "text-red-600"
                }`}
              >
                Since Last Week{" "}
                {dataPointProgress?.sinceLastWeek < 0
                  ? Math.ceil(dataPointProgress?.sinceLastWeek) * -1
                  : Math.ceil(dataPointProgress?.sinceLastWeek)}
                %
              </p>
            </div>
          </div>
        )}
      </div>
    </Card>
  );
}
